import React, {useCallback, useEffect} from "react";
import {FormProvider, useForm} from "react-hook-form";
import {trackPromise, usePromiseTracker} from "react-promise-tracker";
import {Subject} from "rxjs";
import {showToast} from "common";
import _ from "lodash";
import {useObservable} from "rxsupport";
import {ReportModal} from "../../form-components/ReportModal";
import {FatOutlineButton} from "common/component/FatOutlineButton";
import {LoadingSpinner} from "common";
import {AnticipatedDischargeDateUpdateInput, DischargeInitiatorOptionMap} from "../../domain";
import {ModalSelectionInput} from "../../form-components/ModalSelectionInput";
import {YesNoOptionMap} from "../../domain";
import {ModalDatePicker} from "../../form-components/ModalDatePicker";
import {ModalTextAreaInput} from "../../form-components/ModalTextAreaInput";
import {ModalCheckboxInput} from "../../form-components/ModalCheckboxInput";

type Props = {
    visible$: Subject<boolean>;
    onFinished: (formValues: AnticipatedDischargeDateUpdateInput | null) => Promise<void>;
};

export function AnticipatedDischargeDateUpdateFormModal({visible$, onFinished}: Props) {
    const visible = useObservable(visible$, false);

    const methods = useForm<AnticipatedDischargeDateUpdateInput>({
        defaultValues: {
            submittedAnticipatedDischargeDate: "",
            newAnticipatedDischargeDateDefined: "",
            clinicalRationale: "",
            dateDischargeDiscussed: "",
            reasonForDischarge: "",
            dischargeInitiator: "",
            clientFamilyAgreesWithDischarge: "",
            reasonForDisagreement: "",
            dateOfLastPlannedSession: "",
            dischargeReportAcknowledgement: false,
        }
    });
    const setValue = methods.setValue;


    const {promiseInProgress} = usePromiseTracker();

    const isCallingBackend = promiseInProgress;

    const submitWithTracking = useCallback((formValues: AnticipatedDischargeDateUpdateInput | null) => trackPromise(onFinished(formValues)), [onFinished]);

    const onCancel = useCallback(async () => {
        showToast.warning(`Discharge Notification submission has been cancelled`);
        methods.reset();
        visible$.next(false);
    }, [methods, visible$]);
    const submit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        await methods.handleSubmit(async (data: AnticipatedDischargeDateUpdateInput) => {
            await submitWithTracking(_.pick(data, [
                'submittedAnticipatedDischargeDate',
                'newAnticipatedDischargeDateDefined',
                'clinicalRationale',
                'dateDischargeDiscussed',
                'reasonForDischarge',
                'dischargeInitiator',
                'clientFamilyAgreesWithDischarge',
                'reasonForDisagreement',
                'dateOfLastPlannedSession',
                'dischargeReportAcknowledgement',
            ]));
            await new Promise(f => setTimeout(f, 500));
            methods.reset();
            visible$.next(false);
        })(e);
    }, [methods, visible$, submitWithTracking]);
    const clientFamilyAgreesWithDischarge = methods.watch('clientFamilyAgreesWithDischarge')
    useEffect(() => {
        if (clientFamilyAgreesWithDischarge !== 'NO') {
            setValue('reasonForDisagreement', '');
        }
    }, [setValue, clientFamilyAgreesWithDischarge])

    const dischargeInitiator = methods.watch('dischargeInitiator')
    useEffect(() => {
        if (dischargeInitiator !== 'PROVIDER') {
            setValue('clinicalRationale', '');
        }
    }, [setValue, dischargeInitiator])


    return (
        <ReportModal visible={visible} title={'Discharge Notification'} onClose={onCancel} maxWidth="max-w-3xl">
            <FormProvider {...methods}>
                <form autoComplete="off" onSubmit={submit}>
                    {!isCallingBackend && (<>
                        <div className="space-y-6">
                            <div className="flex flex-col w-full p-4 scrollbar-always"
                                 style={{maxHeight: "calc(100vh - 68px - 100px - 37px - 90px - 60px)"}}>
                                <ModalDatePicker<AnticipatedDischargeDateUpdateInput>
                                    labelPosition="top"
                                    label={'Discharge Date'}
                                    disabled={promiseInProgress}
                                    placeholder="Please choose date"
                                    field={'submittedAnticipatedDischargeDate'}
                                    minBusinessDaysInPast={10}
                                />
                                <ModalSelectionInput<AnticipatedDischargeDateUpdateInput>
                                    labelPosition="top"
                                    options={DischargeInitiatorOptionMap}
                                    label={"Who Initiated Discharge?"}
                                    disabled={promiseInProgress}
                                    field={'dischargeInitiator'}
                                    required
                                    placeholder="Select Who"
                                />
                                {dischargeInitiator === 'PROVIDER' && (
                                    <ModalTextAreaInput
                                        labelPosition="top"
                                        label={'Clinical Rationale'}
                                        disabled={promiseInProgress}
                                        field={'clinicalRationale'}
                                        placeholder="Please include client specific rationale for initiating discharge"
                                        required
                                        rowsNum={4}
                                        maxLength={4000}
                                        showCharacterCount
                                        shouldStopInputOnMaxLength={false}
                                    />)
                                }
                                <>
                                    <div className="border-b border-gray-300 my-4"></div>
                                    <ModalDatePicker<AnticipatedDischargeDateUpdateInput>
                                        labelPosition="top"
                                        label={'Date Discharge Discussed with client/family'}
                                        disabled={promiseInProgress}
                                        field={'dateDischargeDiscussed'}
                                        required
                                        placeholder="Please choose date"
                                    />
                                    <ModalTextAreaInput
                                        labelPosition="top"
                                        showCharacterCount
                                        label={'Reason for Discharge'}
                                        disabled={promiseInProgress}
                                        field={'reasonForDischarge'}
                                        required
                                        placeholder="Please describe the details of your discussion with the client/caregiver"
                                        rowsNum={4}
                                        maxLength={4000}
                                        shouldStopInputOnMaxLength={false}
                                    />
                                    <ModalSelectionInput<AnticipatedDischargeDateUpdateInput>
                                        labelPosition="top"
                                        options={YesNoOptionMap}
                                        label={"Is Client/Family in Agreement?"}
                                        disabled={promiseInProgress}
                                        field={'clientFamilyAgreesWithDischarge'}
                                        required
                                        placeholder="Select one"
                                    />
                                    {
                                        clientFamilyAgreesWithDischarge === 'NO' &&
                                        <ModalTextAreaInput
                                            labelPosition="top"
                                            label={'Why the disagreement?'}
                                            disabled={promiseInProgress}
                                            field={'reasonForDisagreement'}
                                            required
                                            rowsNum={4}
                                            maxLength={4000}
                                            showCharacterCount
                                            shouldStopInputOnMaxLength={false}
                                        />
                                    }
                                    <ModalDatePicker<AnticipatedDischargeDateUpdateInput>
                                        labelPosition="top"
                                        label={'Date of Last Planned Session:'}
                                        disabled={promiseInProgress}
                                        field={'dateOfLastPlannedSession'}
                                        required
                                        placeholder="Please choose date"
                                    />
                                    <ModalCheckboxInput
                                        label="The Discharge Report will be sent within 10 business days from the date of the last billed session"
                                        disabled={promiseInProgress}
                                        name="dischargeReportAcknowledgement"
                                        required
                                        validationRules={{
                                            required: 'Please acknowledge the discharge report criteria before proceeding'
                                        }}
                                    />
                                </>
                            </div>
                        </div>

                        <div className="w-full flex border-t dark:border-gray-600 p-4 justify-end">
                            <div className="flex w-1/4">
                                <FatOutlineButton color={'red'}
                                                  text={"Cancel"}
                                                  onClick={onCancel}/>
                                <span style={{width: 10}}/>
                                <FatOutlineButton type="submit" text={"Submit"} color={"green"}/>
                            </div>
                        </div>
                    </>)}

                    {
                        isCallingBackend &&
                        <div className="p-24">
                            <LoadingSpinner text="Processing"/>
                        </div>
                    }
                </form>
            </FormProvider>
        </ReportModal>
    );
}
